/**
 * A7Y-3004 — Feature status vocabulary + the feature registry.
 *
 * One module holds both the status vocabulary (Beta / New / GA, plus the
 * server-side expiry resolver) and the registry that binds a stable feature key
 * to its display surfaces. It is consumed by five surfaces — `a7y-api`,
 * `a7y-webapp`, `a7y-admin`, `a7y-functions` and `a7y-content` — and `packages/types`
 * is the only package all five already depend on. `a7y-content` (which also
 * depends on it) takes over the docs markers once it renders docs (A7Y-4470).
 *
 * It has no import from `@a7y/api-client` (that package depends on this one, so
 * the reverse would be circular), which is why `docsId` is a plain string
 * cross-checked by a test rather than a `DocsPageKey`.
 */

// ======================================================================================|
//  MARK: Vocabulary

export type FeatureStatusValue = 'coming_soon' | 'beta' | 'new' | 'ga';

/**
 * The subset the docs site (`a7y-content`, /docs) can render. `docsStatus`
 * is narrowed to this so the page title's `marker === 'beta' ? 'Beta' : 'New'`
 * stays exhaustive:
 * a `coming_soon` feature has no docs page by construction (A7Y-3046 D2).
 */
export type DocsFeatureStatusValue = 'beta' | 'new' | 'ga';

export type FeatureOwner = 'yevhen' | 'tpf';

/** Days from a Beta start to its graduation review. */
export const BETA_REVIEW_WINDOW_DAYS = 60;
/** Days a feature carries the "New" label before it silently becomes GA. */
export const NEW_WINDOW_DAYS = 30;

// ======================================================================================|
//  MARK: Coming Soon (A7Y-3046)

/**
 * Admin-editable teaser content for a `coming_soon` feature.
 *
 * `plan` and `stores` are `string | null`, not `string | undefined`: the API
 * write is a masked merge whose `updateMask` is built from the payload's own
 * LEAF paths, so an omitted `comingSoon.plan` does not clear a previously
 * stored one (A7Y-3046 D20, one level down). `setFeatureStatus` therefore
 * normalises both to an explicit `null` — the same "clear by writing null,
 * never by omission" rule the top-level fields already follow. Every reader
 * treats them as optional free text, so `null` and `undefined` render alike.
 */
export interface ComingSoonDetail {
  /** A season in plain words, e.g. "Autumn 2026". Never an exact date. */
  expected: string;
  /** Which tier it lands in. Free text; optional. */
  plan?: string | null;
  /** Which stores it covers. Free text; optional. */
  stores?: string | null;
  teaser: { title: string; body: string };
}

export type WaitingListEvent = 'shipped' | 'pulled';

/**
 * Outcome of ONE waiting-list drain pass, returned by the admin
 * `POST /feature-status/:key` when that save flipped a feature away from
 * `coming_soon` (A7Y-3046 D5).
 *
 * Declared here because `a7y-api` authors it and `a7y-admin` renders it, and
 * `a7y-admin` cannot import from `a7y-api` — the only alternative is two
 * hand-kept copies of one wire shape, which is the drift this package exists
 * to prevent. The DURABLE half of the same fact,
 * `PendingWaitingListNotification`, already lives here, so splitting the two
 * across packages would have been the odd choice.
 *
 * `a7y-api`'s `NotifyResult` is an alias of this type, not a second copy.
 */
export interface WaitingListNotifyResult {
  event: WaitingListEvent;
  attempted: number;
  sent: number;
  failed: number;
  /** Recipients still unsent after this pass — what lands in `pendingNotification`. */
  remaining: number;
}

export interface PendingWaitingListNotification {
  event: WaitingListEvent;
  /** ISO timestamp the flip that armed this happened. */
  since: string;
  /** Recipients still unsent at the end of the last drain attempt. */
  unsent: number;
  /** Drain passes performed so far. Bumped on every pass that leaves work. */
  attempts: number;
}

/**
 * Stored shape. One Firestore doc per feature key in COLLECTION.featureStatus.
 * A7Y-3046 D19: the doc id is `${env}__${featureKey}`, not the bare key —
 * every a7y-api environment shares one Firestore database. `featureKey` below
 * stays the bare key and remains what every consumer maps on.
 */
export interface FeatureStatusRecord {
  featureKey: string;
  status: FeatureStatusValue;
  /** ISO date (YYYY-MM-DD) the current status was set. */
  since: string;
  /**
   * ISO date. Required when status === 'beta' and when status ===
   * 'coming_soon' (A7Y-3046 D21). Explicitly `null` for the statuses that have
   * no review date — NOT omitted, for the same masked-merge reason as
   * `comingSoon` below (A7Y-3046 D20/F8).
   */
  reviewBy?: string | null;
  /**
   * ISO date. Written by the API when status is set to 'new'; never
   * client-supplied. Explicitly `null` for every other status (D20/F8).
   */
  newUntil?: string | null;
  knownGaps?: string[];
  owner?: FeatureOwner;
  /**
   * Required when status === 'coming_soon'. Explicitly `null` otherwise —
   * NOT omitted: the write is a masked merge and omission does not clear
   * (A7Y-3046 D20).
   */
  comingSoon?: ComingSoonDetail | null;
  /**
   * Set by the API when a flip away from `coming_soon` did not reach every
   * recipient. Written back as `null` the moment the list is fully sent (D20).
   */
  pendingNotification?: PendingWaitingListNotification | null;
  updatedAt: string;
  updatedBy: string;
}

/**
 * Wire shape carried on `globalConfig.featureStatus`.
 *
 * `status` is ALREADY effective — `new` → `ga` expiry is evaluated server-side
 * against one clock, so `newUntil` never reaches the browser and no client
 * compares a date. `reviewBy` and `owner` are internal scheduling fields and are
 * likewise dropped from the projection.
 */
export interface FeatureStatusPublic {
  status: FeatureStatusValue;
  since: string;
  knownGaps?: string[];
  /** Projected ONLY when the effective status is 'coming_soon'. */
  comingSoon?: ComingSoonDetail;
}
export type FeatureStatusMap = Record<string, FeatureStatusPublic>;

export function isFeatureStatusValue(v: unknown): v is FeatureStatusValue {
  return v === 'coming_soon' || v === 'beta' || v === 'new' || v === 'ga';
}

export function isDocsFeatureStatusValue(v: unknown): v is DocsFeatureStatusValue {
  return v === 'beta' || v === 'new' || v === 'ga';
}

// ======================================================================================|
//  MARK: Date helpers

/** UTC date-only (YYYY-MM-DD) rendering. One clock, no local-timezone drift. */
function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDays(now: Date, days: number): string {
  const d = new Date(now.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return toIsoDate(d);
}

/** `now` + BETA_REVIEW_WINDOW_DAYS, date-only. */
export function defaultReviewBy(now: Date): string {
  return addDays(now, BETA_REVIEW_WINDOW_DAYS);
}

/** `now` + NEW_WINDOW_DAYS, date-only. */
export function defaultNewUntil(now: Date): string {
  return addDays(now, NEW_WINDOW_DAYS);
}

/**
 * Best-effort last day of a plain-words period, for pre-filling the admin's
 * `reviewBy` input. Returns null when the text is not recognised — it NEVER
 * guesses, and the server never derives `reviewBy` from it (A7Y-3046 D21).
 *
 * Case- and whitespace-insensitive. Northern-hemisphere meteorological
 * seasons; "Winter YYYY" spans Dec YYYY – Feb YYYY+1 and therefore ends in
 * YYYY+1 (leap years computed, never hardcoded).
 *
 *   spring YYYY        -> YYYY-05-31      Q1..Q4 YYYY -> quarter end
 *   summer YYYY        -> YYYY-08-31      H1 YYYY     -> YYYY-06-30
 *   autumn|fall YYYY   -> YYYY-11-30      H2 YYYY     -> YYYY-12-31
 *   winter YYYY        -> (YYYY+1)-02-28|29
 *   early|mid|late YYYY-> YYYY-04-30 | YYYY-08-31 | YYYY-12-31
 *   <Month> YYYY       -> last day of that month
 *   YYYY               -> YYYY-12-31
 *   anything else      -> null
 */
export function seasonEndDate(expected: string): string | null {
  if (typeof expected !== 'string') return null;
  const text = expected.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) return null;

  const yearMatch = text.match(/(?:^|[^0-9])((?:19|20)\d{2})(?:[^0-9]|$)/);
  if (!yearMatch) return null;
  const year = Number(yearMatch[1]);

  const words = text.replace(/((?:19|20)\d{2})/g, ' ').trim();

  // `YYYY` alone.
  if (!words) return endOfMonth(year, 11);

  const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];
  const monthIndex = MONTHS.findIndex(
    (m) => words === m || words === m.slice(0, 3) || words === `${m.slice(0, 3)}.`
  );
  if (monthIndex >= 0) return endOfMonth(year, monthIndex);

  switch (words) {
    case 'spring':
      return endOfMonth(year, 4); // May
    case 'summer':
      return endOfMonth(year, 7); // August
    case 'autumn':
    case 'fall':
      return endOfMonth(year, 10); // November
    case 'winter':
      return endOfMonth(year + 1, 1); // February of the following year
    case 'early':
      return endOfMonth(year, 3); // April
    case 'mid':
    case 'middle of':
      return endOfMonth(year, 7); // August
    case 'late':
    case 'end of':
      return endOfMonth(year, 11); // December
    case 'q1':
      return endOfMonth(year, 2);
    case 'q2':
      return endOfMonth(year, 5);
    case 'q3':
      return endOfMonth(year, 8);
    case 'q4':
      return endOfMonth(year, 11);
    case 'h1':
      return endOfMonth(year, 5);
    case 'h2':
      return endOfMonth(year, 11);
    default:
      return null;
  }
}

/** Last day of `monthIndex` (0-based) in `year`, as YYYY-MM-DD. Leap-year safe. */
function endOfMonth(year: number, monthIndex: number): string {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).toISOString().slice(0, 10);
}

/**
 * `coming_soon`, `beta` and `ga` pass through untouched — **Beta never expires**; it graduates
 * by a human decision, prompted by the daily review job.
 *
 * `new` becomes `ga` once `newUntil` is at or before `now`. A `new` record with
 * no `newUntil` stays `new`; the API always writes one, so that shape only
 * occurs for a hand-edited document.
 */
export function resolveEffectiveFeatureStatus(
  record: Pick<FeatureStatusRecord, 'status' | 'newUntil'> | undefined,
  now: Date
): FeatureStatusValue {
  if (!record || !isFeatureStatusValue(record.status)) return 'ga';
  if (record.status !== 'new') return record.status;
  if (!record.newUntil) return 'new';
  return record.newUntil <= toIsoDate(now) ? 'ga' : 'new';
}

// ======================================================================================|
//  MARK: Registry

/** Sidebar sections, matching SidebarNav's five render regions exactly.
 *  'top' is the unlabeled block above the first SidebarHeading. The other
 *  four match the `data-testid="sidebar-heading-*"` suffixes. */
export type FeatureSidebarSection = 'top' | 'discovery' | 'assets' | 'editor' | 'archives';

/** Icon-by-name. `packages/types` cannot hold heroicon components, so the
 *  vocabulary is a closed union here and an exhaustive map in a7y-webapp
 *  (`COMING_SOON_ICONS`), which makes an unmapped name a compile error (D10). */
export type FeatureSidebarIconName =
  | 'beaker'
  | 'calendar-days'
  | 'megaphone'
  | 'rectangle-group'
  | 'sparkles'
  | 'document-duplicate';

/**
 * How a feature binds to the sidebar. `existing` names a route that already
 * renders; `coming_soon` (A7Y-3046) carries its own placement for a feature
 * that has no route yet.
 */
export type FeatureSidebarBinding =
  | { kind: 'existing'; destination: string }
  | {
      kind: 'coming_soon';
      section: FeatureSidebarSection;
      icon: FeatureSidebarIconName;
      /** Present only on the reserved mechanism-test key. The API refuses to
       *  set `coming_soon` on such an entry when computeEnvFlags(c.env).isProd
       *  (D8 layer 3). Environment isolation itself is D19's job, not this
       *  flag's — this is defence in depth against a production admin click. */
      mechanismTest?: true;
    };

export interface FeatureRegistryEntry {
  /** Stable. It is the Firestore doc id, the Mixpanel `feature` prop, and the admin row id. Never renamed. */
  key: string;
  label: string;
  /** Shown on the admin row. Replaces a7y-admin's hardcoded per-flag description map. */
  description: string;
  /**
   * Exact `PROJECT_PATHS` value. Matched by EQUALITY against a sidebar item's
   * `destination` and by segment-boundary matching against the browser
   * pathname (A7Y-3709 — see `featureKeyForPathname`).
   */
  pathSegment?: string;
  /**
   * Additional bound path segments beyond `pathSegment` (A7Y-3709). Same
   * matching contract as `pathSegment` in every resolver: sidebar-destination
   * equality and pathname/href segment-boundary matching alike.
   */
  extraPathSegments?: readonly string[];
  /** Sidebar placement. A `coming_soon` binding (A7Y-3046) renders a dimmed
   *  row and a teaser route for an entry that has no `pathSegment`. */
  sidebar?: FeatureSidebarBinding;
  /** The docs page slug (the old docs site's content-collection entry id) — i.e. a value of DOCS_PAGES, e.g. 'audit-engines/ai-discovery-engine'. */
  docsId?: string;
  /**
   * DOCS-ONLY compiled marker (A7Y-3004 D18). Drives the docs site's Beta/New
   * marker and NOTHING ELSE. The webapp never reads it: `useFeatureStatus`
   * falls back to 'ga', so no pill, no banner and no Mixpanel event can ever
   * fire off a compiled guess — which matters because `feature_beta_viewed` is
   * the sole input to the "≥ 20 distinct accounts" Beta exit criterion the
   * graduation reminder thresholds on. Undefined => no docs marker. The daily
   * reminder job reports divergence between this and the live status.
   */
  docsStatus?: DocsFeatureStatusValue;
  defaultOwner: FeatureOwner;
  /**
   * Marks the entry as switchable from the org manager's Features page
   * (A7Y-3709). Effective visibility is `effectiveFeatureVisibility` —
   * the org's stored `settings.features[key]` when set, otherwise the
   * default below.
   */
  orgSwitchable?: true;
  /** Org switch only: ignore persisted launch status and all status overrides. */
  orgOnly?: true;
  /**
   * A7Y-3723 — the org switch defaults OFF for this entry in EVERY
   * environment, whatever its live status. Only meaningful alongside
   * `orgSwitchable`.
   *
   * Without it the default is derived from the status (`off` while Beta, `on`
   * otherwise), which couples two unrelated decisions: how mature a feature is,
   * and whether an org that has never touched the switch should see it. That
   * coupling is what hid the Events surface — `beta` was written to switch the
   * feature ON for Sandbox and instead closed the org gate for every org, while
   * `ga` would have opened it for every org. Neither value can express "shipped,
   * opt-in", which is what a surface with live ASC write paths needs.
   */
  defaultOff?: true;
}

/**
 * Ordered most-specific `pathSegment` first; `featureKeyForPathname` returns the
 * first match.
 *
 * No entry declares `docsStatus`, and no Firestore document is seeded — merging
 * A7Y-3004 changes nothing visible in any surface. Which features are Beta is
 * Yevhen's product judgement, set from the admin page without a deploy. A unit
 * test pins the absence, so restoring one is a conscious edit.
 *
 * A7Y-3046 adds exactly one `coming_soon`-bound entry, `soon-mechanism-test`,
 * and it ships dark: no Firestore document is seeded, so nothing renders until
 * a super_admin flips it — and the API refuses that flip in production (D8).
 * The entry has no `pathSegment`, so it is invisible to `featureKeyForPathname`
 * and to every routed-entry assertion.
 */
export const FEATURE_REGISTRY: readonly FeatureRegistryEntry[] = [
  {
    key: 'asset-editor-store-text',
    label: 'Asset Editor: Store Text',
    description:
      'The store-text tab of the Asset Editor — title, subtitle, description, keywords and promotional text editing with AI suggestions.',
    pathSegment: 'editor/asset-editor/store-text',
    docsId: 'editing/asset-editor-store-text',
    defaultOwner: 'tpf',
  },
  {
    key: 'ai-discoverability',
    label: 'AI Discoverability',
    description:
      'The AI Discovery engine surface — how the app is described by AI assistants and where it is absent from their answers.',
    pathSegment: 'discovery/ai-discoverability',
    docsId: 'audit-engines/ai-discovery-engine',
    defaultOwner: 'yevhen',
  },
  {
    key: 'user-sentiment',
    label: 'User Sentiment',
    description:
      'The Sentiment engine surface — review-derived themes, complaints and praise, with competitor comparison.',
    pathSegment: 'market/user-sentiment',
    docsId: 'audit-engines/sentiment-engine',
    defaultOwner: 'yevhen',
  },
  {
    key: 'keywords',
    label: 'Keywords & Clusters',
    description:
      'The keyword workspace — intended, implied, suggested and competitor keywords with scoring and rank history.',
    pathSegment: 'discovery/keywords',
    // The sidebar's Keywords row lands on the Analysis tab (A7Y-5791).
    extraPathSegments: ['discovery/keywords/analysis'],
    docsId: 'keywords/workspace',
    defaultOwner: 'yevhen',
  },
  {
    key: 'competitors',
    label: 'Competitors',
    description:
      'The competitor workspace — the tracked competitor set, their activity and store-listing snapshots.',
    pathSegment: 'market/competitors',
    docsId: 'competitors/managing-competitors',
    defaultOwner: 'yevhen',
  },
  {
    key: 'soon-mechanism-test',
    label: 'Coming Soon (test)',
    description:
      'Reserved mechanism-test key for the Coming Soon surface (A7Y-3046). Not a product feature. ' +
      'Setting this to Soon renders a dimmed sidebar row and a teaser page for every signed-in user ' +
      'IN THIS ENVIRONMENT ONLY (A7Y-3046 D19 scopes the Firestore document per environment); ' +
      'the API refuses it in production regardless.',
    sidebar: { kind: 'coming_soon', section: 'archives', icon: 'beaker', mechanismTest: true },
    defaultOwner: 'tpf',
  },
  // A7Y-3709 — org-switchable entries (org manager Features page). Amendment
  // A5: a `pathSegment` is not inert — it enrols these routes'
  // `AuditPageHeader` into the pill / Beta banner / `feature_beta_viewed`
  // path where it previously resolved null. Accepted deliberately: these
  // routes ARE the feature, so a Beta feature showing its own Beta banner is
  // the A7Y-3004 mechanism working as designed.
  {
    key: 'publishing',
    label: 'Publishing',
    description:
      'The Publishing workspace — draft-listing editing and submission, plus its publishing history of previously submitted versions.',
    pathSegment: 'editor/draft-listing',
    extraPathSegments: ['archives/publishing-history'],
    orgSwitchable: true,
    defaultOwner: 'yevhen',
  },
  {
    key: 'in-app-events',
    label: 'In-App Events & Promotional Content',
    description:
      'The in-app events workspace — scheduling and managing on-device events and Promotional Content shown in the App Store and Play Store.',
    pathSegment: 'events',
    orgSwitchable: true,
    orgOnly: true,
    // A7Y-3723: opt-in per org in every environment. The surface writes ASC
    // drafts and can submit to App Review, so an org that has never switched it
    // on must not see it.
    defaultOff: true,
    defaultOwner: 'tpf',
  },
  {
    key: 'launch-readiness',
    label: 'Launch Readiness Check',
    description: 'Check your unpublished listing text and creatives before launch.',
    orgSwitchable: true,
    orgOnly: true,
    defaultOff: true,
    defaultOwner: 'tpf',
  },
];

export function featureByKey(key: string): FeatureRegistryEntry | undefined {
  return FEATURE_REGISTRY.find((entry) => entry.key === key);
}

/**
 * Segment-boundary match: `path` contains `/${seg}/` or ends with `/${seg}`.
 * (A7Y-3709.) Chosen over `includes` because a bare single-word `pathSegment`
 * (e.g. `'events'`) can otherwise occur as a substring of an org/project id
 * that shares the pathname prefix.
 */
function pathContainsSegment(path: string, seg: string): boolean {
  return path.includes(`/${seg}/`) || path.endsWith(`/${seg}`);
}

/**
 * Pathname/href → feature key. Segment-boundary match (see
 * `pathContainsSegment`) over `[pathSegment, ...(extraPathSegments ?? [])]`
 * for each entry, registry ordered most-specific first, first match wins.
 *
 * Used for BOTH an absolute app pathname (`AuditPageHeader`, via
 * `useLocation().pathname`) and an absolute href string (the sidebar
 * wrapper) — one implementation, no separate resolver (A7Y-3709 amendment
 * A1). Contract change from the prior `includes`-based match: a bare
 * single-word segment like `'events'` no longer matches a substring inside
 * an unrelated path id.
 */
export function featureKeyForPathname(pathname: string): string | null {
  if (!pathname) return null;
  for (const entry of FEATURE_REGISTRY) {
    const segments = entry.pathSegment ? [entry.pathSegment, ...(entry.extraPathSegments ?? [])] : [];
    if (segments.some((seg) => pathContainsSegment(pathname, seg))) return entry.key;
  }
  return null;
}

/**
 * Sidebar `destination` → feature key. Strict equality against
 * `pathSegment` or any `extraPathSegments` entry — destinations are exact
 * `PROJECT_PATHS` constants.
 */
export function featureKeyForSidebarDestination(destination: string): string | null {
  if (!destination) return null;
  const entry = FEATURE_REGISTRY.find(
    (e) => e.pathSegment === destination || e.extraPathSegments?.includes(destination)
  );
  return entry ? entry.key : null;
}

/**
 * The org's explicit switch always wins. With no stored value the default is
 * `false` for a `defaultOff` entry (A7Y-3723) and otherwise `status !== 'beta'`
 * (A7Y-3709 — a Beta feature defaults off, everything else defaults on).
 *
 * `defaultOff` is passed in rather than looked up here so this stays a pure
 * function of its arguments; `featureDefaultsOff` is the lookup.
 */
export function effectiveFeatureVisibility(
  stored: boolean | undefined,
  status: FeatureStatusValue,
  defaultOff = false
): boolean {
  if (stored !== undefined) return stored;
  return defaultOff ? false : status !== 'beta';
}

/** Whether `key`'s org switch defaults off regardless of status (A7Y-3723). */
export function featureDefaultsOff(key: string): boolean {
  return featureByKey(key)?.defaultOff === true;
}

/** Registry entries switchable from the org manager's Features page. */
export function orgSwitchableFeatures(): readonly FeatureRegistryEntry[] {
  return FEATURE_REGISTRY.filter((entry) => entry.orgSwitchable === true);
}

/** Whether `key` is a registered org-switchable feature. */
export function isOrgSwitchableFeatureKey(key: string): boolean {
  return orgSwitchableFeatures().some((entry) => entry.key === key);
}

/** Docs page slug → feature key. Strict equality. */
export function featureKeyForDocsId(docsId: string): string | null {
  if (!docsId) return null;
  const entry = FEATURE_REGISTRY.find((e) => e.docsId === docsId);
  return entry ? entry.key : null;
}
